import { useLocalStorage } from '@/composables/useLocalStorage.js';

const { setLS, getLS } = useLocalStorage();

export const soulodyApi = {
  namespaced: true,
  state: () => ({
    accessToken: null,
    expiresAt: null,
    partnerId: null,
    rateLimit: 0,
    rateLimitRemaining: 0,
    rateLimitReset: null,
    scope: null,
    tokenType: null,
    tokenPromise: null
  }),

  getters: {
    accessToken: state => state.accessToken,
    isTokenValid: state => {
      if (!state.accessToken || !state.expiresAt) {
        return false;
      }
      const expiry =
        state.expiresAt instanceof Date ? state.expiresAt.getTime() : Number(state.expiresAt);
      return expiry > Date.now() + 30000;
    }
  },

  mutations: {
    setAccessToken(state, accessToken) {
      state.accessToken = accessToken;
      setLS('soulodyApi._token', accessToken);
    },

    setExpiresAt(state, expiresAt) {
      if (!expiresAt) {
        return;
      }
      const dateObj = new Date(Number(expiresAt) || expiresAt);
      state.expiresAt = dateObj;
      setLS('soulodyApi._token_expiration', dateObj.getTime());
    },

    setTokenPromise(state, promise) {
      state.tokenPromise = promise;
    },

    setApiMetadata(state, payload) {
      state.partnerId = payload.partner_id;
      state.rateLimit = payload.rate_limit;
      state.rateLimitRemaining = payload.rate_limit_remaining;
      state.scope = payload.scope;
      state.tokenType = payload.token_type;
      if (payload.rate_limit_reset) {
        state.rateLimitReset = new Date(payload.rate_limit_reset);
      }
    },
    resetStore(state) {
      state.accessToken = null;
      state.expiresAt = null;
      state.partnerId = null;
      state.rateLimit = 0;
      state.rateLimitRemaining = 0;
      state.rateLimitReset = null;
      state.scope = null;
      state.tokenType = null;
      state.tokenPromise = null;
      // Token + expiry persist to localStorage via setAccessToken/setExpiresAt;
      // clear those too so `initStore` doesn't re-hydrate after logout.
      setLS('soulodyApi._token', undefined);
      setLS('soulodyApi._token_expiration', undefined);
    }
  },

  actions: {
    async ensureValidToken({ dispatch, state, commit }) {
      if (state.accessToken && state.expiresAt && Date.now() < state.expiresAt.valueOf()) {
        return true;
      }

      if (!state.tokenPromise) {
        commit('setTokenPromise', dispatch('getToken'));
      }

      try {
        return await state.tokenPromise;
      } finally {
        commit('setTokenPromise', null);
      }
    },

    async getToken({ commit, dispatch, state }) {
      const apiKey = import.meta.env.VITE_API_KEY_APP;
      if (!apiKey) {
        const err = new Error('Missing VITE_API_KEY_APP');
        await dispatch(
          'errorModal/handleError',
          {
            error: err,
            code: '0x00000082',
            context: { env: import.meta.env.MODE }
          },
          { root: true }
        );
        return false;
      }

      try {
        const data = await this.$soulodyApi.post('token', {
          api_key: import.meta.env.VITE_API_KEY_APP
        });

        if (data?.access_token) {
          commit('setAccessToken', data.access_token);
          const absoluteExpiry = Date.now() + data.expires_in * 1000;
          commit('setExpiresAt', absoluteExpiry);
          commit('setApiMetadata', data);
          return true;
        }

        throw new Error('Invalid token structure');
      } catch (err) {
        const errorContext = {
          endpoint: 'POST /token',
          currentInternalToken: state.accessToken
        };

        await dispatch(
          'errorModal/handleError',
          {
            error: err,
            code: '0x00000067',
            context: errorContext
          },
          { root: true }
        );

        return false;
      }
    },
    setTokenData({ commit }, payload) {
      commit('setAccessToken', payload.access_token);
      const absoluteExpiry = Date.now() + payload.expires_in * 1000;
      commit('setExpiresAt', absoluteExpiry);
      commit('setApiMetadata', payload);
    },

    async initStore({ commit, dispatch }) {
      const token = getLS('soulodyApi._token');
      const expiresAt = getLS('soulodyApi._token_expiration');

      if (token && expiresAt) {
        commit('setAccessToken', token);
        commit('setExpiresAt', expiresAt);
        await dispatch('ensureValidToken');
      }
    }
  }
};
